import { Button, Icon, useT } from "@mmm/ui";
export type ToastVariant = "error" | "success" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  variant: ToastVariant;
  title: string;
  message?: string;
  action?: ToastAction;
  onDismiss: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Focus entering the toast from outside it. */
  onFocusEnter?: () => void;
  /** Focus leaving the toast entirely — not moving between its own buttons. */
  onFocusLeave?: () => void;
}

/**
 * Single toast notification. Presentational only — timing (auto-dismiss,
 * hover-pause) and stacking live in ToastProvider; this component just
 * renders one item and reports dismiss/hover events upward.
 */
export function Toast({
  variant,
  title,
  message,
  action,
  onDismiss,
  onMouseEnter,
  onMouseLeave,
  onFocusEnter,
  onFocusLeave,
}: ToastProps) {
  const t = useT();
  // Errors interrupt (assertive + alert); success/info are ambient confirmations.
  const isError = variant === "error";

  return (
    <div
      className={`mmm-toast mmm-toast--${variant}`}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // React's onFocus/onBlur are focusin/focusout, so they fire for the
      // buttons inside. relatedTarget is what keeps Tab between the action and
      // Dismiss from counting as leaving — that would restart the countdown
      // halfway through reading it.
      onFocus={onFocusEnter}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onFocusLeave?.();
      }}
    >
      <div className="mmm-toast-body">
        <p className="mmm-toast-title">{title}</p>
        {message && <p className="mmm-toast-message">{message}</p>}
        {action && (
          <Button
            variant="text"
            className="mmm-toast-action"
            onClick={() => {
              action.onClick();
              onDismiss();
            }}
          >
            {action.label}
          </Button>
        )}
      </div>
      <Button
        variant="text"
        className="mmm-toast-dismiss"
        aria-label={t("common.dismissNotification")}
        title={t("common.dismissNotification")}
        onClick={onDismiss}
      >
        <Icon name="close" />
      </Button>
    </div>
  );
}
